import { TOKEN } from './constants'

/** Shape aligned with pump.fun /api/ipfs metadata JSON. */
export type TokenMetadataInput = {
  name: string
  symbol: string
  description: string
  website?: string
  twitter?: string
  telegram?: string
  showName?: boolean
  /** Optional custom logo; defaults to fetching /logo.png. */
  imageFile?: File | Blob | null
}

export type UploadMetadataResult = {
  metadataUri: string
  imageUri?: string
  metadata?: Record<string, unknown>
  /** Which upload backend succeeded. */
  via: 'same-origin' | 'pump.fun' | 'pinata'
}

const PUMP_IPFS = 'https://pump.fun/api/ipfs'

/** Same-origin paths: Vite proxy in dev/preview; PHP proxy on Hostinger. */
const SAME_ORIGIN_ENDPOINTS = ['/ipfs-upload', '/ipfs-upload.php'] as const

function pinataJwt(): string | undefined {
  const v = import.meta.env.VITE_PINATA_JWT
  return typeof v === 'string' && v.trim() ? v.trim() : undefined
}

async function resolveImageFile(
  imageFile?: File | Blob | null,
): Promise<{ blob: Blob; filename: string; type: string }> {
  if (imageFile) {
    const type = imageFile.type || 'image/png'
    const filename =
      imageFile instanceof File && imageFile.name
        ? imageFile.name
        : type.includes('webp')
          ? 'logo.webp'
          : type.includes('jpeg') || type.includes('jpg')
            ? 'logo.jpg'
            : 'logo.png'
    return { blob: imageFile, filename, type }
  }

  const res = await fetch(TOKEN.logoPath)
  if (!res.ok) {
    throw new Error(
      `Could not load default logo (${TOKEN.logoPath}): HTTP ${res.status}`,
    )
  }
  const blob = await res.blob()
  return {
    blob,
    filename: 'logo.png',
    type: blob.type || 'image/png',
  }
}

function buildPumpFormData(
  input: TokenMetadataInput,
  image: { blob: Blob; filename: string; type: string },
): FormData {
  const fd = new FormData()
  fd.append('file', image.blob, image.filename)
  fd.append('name', input.name.trim())
  fd.append('symbol', input.symbol.trim())
  fd.append('description', input.description.trim())
  fd.append('twitter', (input.twitter || '').trim())
  fd.append('telegram', (input.telegram || '').trim())
  fd.append('website', (input.website || '').trim())
  fd.append('showName', input.showName === false ? 'false' : 'true')
  return fd
}

type PumpIpfsJson = {
  metadataUri?: string
  metadata?: {
    image?: string
    name?: string
    symbol?: string
    description?: string
    website?: string
    showName?: boolean
    createdOn?: string
  }
}

type PostResult = {
  ok: boolean
  status: number
  json: PumpIpfsJson | null
  raw: string
  corsLikely: boolean
}

async function postFormData(url: string, formData: FormData): Promise<PostResult> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
    })
    const raw = await res.text()
    let json: PumpIpfsJson | null = null
    try {
      json = raw ? (JSON.parse(raw) as PumpIpfsJson) : null
    } catch {
      json = null
    }
    return { ok: res.ok, status: res.status, json, raw, corsLikely: false }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    const corsLikely =
      msg.toLowerCase().includes('failed to fetch') ||
      msg.toLowerCase().includes('networkerror') ||
      msg.toLowerCase().includes('cors')
    return { ok: false, status: 0, json: null, raw: msg, corsLikely }
  }
}

function resultFromPump(
  post: PostResult,
  via: UploadMetadataResult['via'],
): UploadMetadataResult | null {
  if (!post.ok || !post.json?.metadataUri) return null
  return {
    metadataUri: post.json.metadataUri,
    imageUri: post.json.metadata?.image,
    metadata: post.json.metadata as Record<string, unknown> | undefined,
    via,
  }
}

/**
 * Pinata uploads (optional). Needs VITE_PINATA_JWT at build time.
 * Used only when same-origin / pump.fun paths fail.
 */
async function uploadViaPinata(
  input: TokenMetadataInput,
  image: { blob: Blob; filename: string; type: string },
  jwt: string,
): Promise<UploadMetadataResult> {
  const imgFd = new FormData()
  imgFd.append('network', 'public')
  imgFd.append('file', image.blob, image.filename)

  const imgRes = await fetch('https://uploads.pinata.cloud/v3/files', {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: imgFd,
  })
  const imgText = await imgRes.text()
  if (!imgRes.ok) {
    throw new Error(
      `Pinata image upload failed (${imgRes.status}): ${imgText.slice(0, 200)}`,
    )
  }
  const imgJson = JSON.parse(imgText) as { data?: { cid?: string }; cid?: string }
  const imageCid = imgJson.data?.cid || imgJson.cid
  if (!imageCid) throw new Error('Pinata image upload returned no CID.')
  const imageUri = `https://ipfs.io/ipfs/${imageCid}`

  const metadata = {
    name: input.name.trim(),
    symbol: input.symbol.trim(),
    description: input.description.trim(),
    image: imageUri,
    showName: input.showName !== false,
    createdOn: 'https://pump.fun',
    ...(input.website?.trim() ? { website: input.website.trim() } : {}),
    ...(input.twitter?.trim() ? { twitter: input.twitter.trim() } : {}),
    ...(input.telegram?.trim() ? { telegram: input.telegram.trim() } : {}),
  }

  const metaFile = new File([JSON.stringify(metadata)], 'metadata.json', {
    type: 'application/json',
  })
  const metaFd = new FormData()
  metaFd.append('network', 'public')
  metaFd.append('file', metaFile)

  const metaRes = await fetch('https://uploads.pinata.cloud/v3/files', {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: metaFd,
  })
  const metaText = await metaRes.text()
  if (!metaRes.ok) {
    throw new Error(
      `Pinata metadata upload failed (${metaRes.status}): ${metaText.slice(0, 200)}`,
    )
  }
  const metaJson = JSON.parse(metaText) as { data?: { cid?: string }; cid?: string }
  const metaCid = metaJson.data?.cid || metaJson.cid
  if (!metaCid) throw new Error('Pinata metadata upload returned no CID.')

  return {
    metadataUri: `https://ipfs.io/ipfs/${metaCid}`,
    imageUri,
    metadata,
    via: 'pinata',
  }
}

/**
 * Upload token metadata + logo so createV2Instruction gets a live uri.
 *
 * Order (keyless first):
 * 1. Same-origin `/ipfs-upload` or `/ipfs-upload.php` (Vite proxy / Hostinger PHP → pump.fun)
 * 2. Direct `https://pump.fun/api/ipfs` (works server-side; browsers often hit CORS)
 * 3. Pinata if `VITE_PINATA_JWT` is set
 *
 * @pump-fun/pump-sdk has no uploadMetadata helper — only accepts uri.
 */
export async function uploadTokenMetadata(
  input: TokenMetadataInput,
): Promise<UploadMetadataResult> {
  const name = input.name.trim()
  const symbol = input.symbol.trim()
  const description = input.description.trim()
  if (!name) throw new Error('Token name is required for metadata upload.')
  if (!symbol) throw new Error('Token symbol is required for metadata upload.')
  if (!description) {
    throw new Error('Description is required for metadata upload.')
  }

  const image = await resolveImageFile(input.imageFile)
  const errors: string[] = []

  for (const path of SAME_ORIGIN_ENDPOINTS) {
    const post = await postFormData(path, buildPumpFormData(input, image))
    const hit = resultFromPump(post, 'same-origin')
    if (hit) return hit
    if (post.status === 0 || post.status === 404) continue
    errors.push(
      `${path}: HTTP ${post.status}${post.raw ? ` — ${post.raw.slice(0, 120)}` : ''}`,
    )
  }

  {
    const post = await postFormData(PUMP_IPFS, buildPumpFormData(input, image))
    const hit = resultFromPump(post, 'pump.fun')
    if (hit) return hit
    if (post.corsLikely) {
      errors.push(
        'pump.fun/api/ipfs blocked by browser CORS (expected from third-party origins).',
      )
    } else if (post.status) {
      errors.push(
        `pump.fun/api/ipfs: HTTP ${post.status} — ${post.raw.slice(0, 120)}`,
      )
    } else {
      errors.push(`pump.fun/api/ipfs: ${post.raw.slice(0, 120)}`)
    }
  }

  const jwt = pinataJwt()
  if (jwt) {
    try {
      return await uploadViaPinata(input, image, jwt)
    } catch (e) {
      errors.push(e instanceof Error ? e.message : String(e))
    }
  }

  throw new Error(
    [
      'Metadata upload failed (description/website/image need a public HTTPS uri before create).',
      'Tried same-origin proxy (/ipfs-upload, /ipfs-upload.php) and pump.fun/api/ipfs.',
      jwt
        ? 'Pinata JWT was set but upload failed.'
        : 'No VITE_PINATA_JWT fallback configured.',
      'Fix: deploy public/ipfs-upload.php on Hostinger (PHP), or set VITE_PINATA_JWT and rebuild.',
      errors.length ? `Details: ${errors.join(' | ')}` : '',
    ]
      .filter(Boolean)
      .join(' '),
  )
}
